import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Skeleton } from 'antd';
import moment from 'moment';

import { ITeacherMiniCard, useCanOpenTeacherCard } from 'core/hooks';
import { PATHS } from 'router/config';

import * as S from '../StudentMiniCard/StudentMiniCard.styled';

interface IProps {
  teacherId: string;
  card: ITeacherMiniCard | null;
  loading: boolean;
  failed: boolean;
  onRetry?: () => void;
}

const date = (iso: string): string => moment(iso).format('DD.MM.YYYY');

/**
 * ТЗ 0122 — «кто это» для преподавателя: тот же вид, что у мини-карточки
 * студента (ТЗ 0009) — группы, ближайшее занятие, рейтинг, открытые
 * задачи, ссылка на досье (если роли оно открыто).
 */
export const TeacherMiniCard = memo<IProps>(
  ({ teacherId, card, loading, failed, onRetry }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const canOpenCard = useCanOpenTeacherCard();

    if (loading && !card) {
      return (
        <S.Card>
          <Skeleton active paragraph={{ rows: 4 }} title={false} />
        </S.Card>
      );
    }
    if (failed || !card) {
      return (
        <S.FailedWrap>
          {failed
            ? t('pages.communicator.loadFailed')
            : t('teacherMiniCard.hidden')}
          {failed && onRetry ? (
            <S.RetryLink type="button" onClick={onRetry}>
              {t('pages.communicator.miniCard.retry')}
            </S.RetryLink>
          ) : null}
        </S.FailedWrap>
      );
    }

    const name = `${card.firstName} ${card.lastName}`.trim();
    return (
      <S.Card>
        <S.Header>
          <S.Name>{name || card.email}</S.Name>
          {card.isDismissed ? (
            <S.StatusTag tone="muted">
              {t('teacherMiniCard.dismissed')}
            </S.StatusTag>
          ) : card.isTeaching ? (
            <S.StatusTag tone="ok">{t('teacherMiniCard.teaching')}</S.StatusTag>
          ) : (
            <S.StatusTag tone="info">{t('teacherMiniCard.idle')}</S.StatusTag>
          )}
        </S.Header>
        <S.Contact>
          {card.email}
          {card.phone ? ` · ${card.phone}` : ''}
        </S.Contact>

        <S.Section>
          <S.SectionTitle>{t('teacherMiniCard.groups')}</S.SectionTitle>
          {card.groups.length === 0 ? (
            <S.Muted>{t('teacherMiniCard.noGroups')}</S.Muted>
          ) : (
            card.groups.map((group) => (
              <S.GroupRow key={group.groupId}>
                <S.GroupName>{group.groupName}</S.GroupName>
                {group.courseName ? (
                  <S.GroupCourse>{group.courseName}</S.GroupCourse>
                ) : null}
                {group.nextLessonDate ? (
                  <S.FlagTag>
                    {t('teacherMiniCard.nextLesson', {
                      date: date(group.nextLessonDate),
                    })}
                  </S.FlagTag>
                ) : null}
                {typeof group.lessonsLeft === 'number' ? (
                  <S.FlagTag>
                    {t('teacherMiniCard.lessonsLeft', { n: group.lessonsLeft })}
                  </S.FlagTag>
                ) : null}
              </S.GroupRow>
            ))
          )}
        </S.Section>

        {card.nextLesson ? (
          <S.Section>
            <S.SectionTitle>{t('teacherMiniCard.closest')}</S.SectionTitle>
            <S.ItemRow>
              <S.ItemTitle>
                {card.nextLesson.name || card.nextLesson.groupName}
              </S.ItemTitle>
              <S.ItemMeta>
                {date(card.nextLesson.date)}
                {card.nextLesson.startTime
                  ? ` ${String(card.nextLesson.startTime).slice(0, 5)}`
                  : ''}
                {' · '}
                {card.nextLesson.groupName}
              </S.ItemMeta>
            </S.ItemRow>
          </S.Section>
        ) : null}

        <S.Section>
          <S.ItemMeta>
            {card.studentRating !== null && card.studentRating !== undefined
              ? t('teacherMiniCard.studentRating', {
                  value: card.studentRating.toFixed(1),
                  n: card.studentRatingCount,
                })
              : t('teacherMiniCard.noStudentRating')}
            {typeof card.rating === 'number'
              ? ` · ${t('teacherMiniCard.rating', { value: card.rating })}`
              : ''}
            {` · ${t('teacherMiniCard.openTasks', { n: card.openTasksCount })}`}
            {` · ${
              card.accessToLms
                ? t('teacherMiniCard.lmsOn')
                : t('teacherMiniCard.lmsOff')
            }`}
          </S.ItemMeta>
        </S.Section>

        {canOpenCard ? (
          <S.Footer>
            <S.OpenLink
              type="button"
              onClick={() =>
                history.push(PATHS.teacherDetail.replace(':id', teacherId))
              }
            >
              {t('teacherMiniCard.open')}
            </S.OpenLink>
          </S.Footer>
        ) : null}
      </S.Card>
    );
  },
);

TeacherMiniCard.displayName = 'TeacherMiniCard';
